import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users, ClipboardList, Plus, ArrowUpRight, Trophy } from "lucide-react";

export default function CoachDashboard() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [cats, setCats] = useState([]);
  const [players, setPlayers] = useState([]);
  const [jornadas, setJornadas] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [t, c, j] = await Promise.all([api.get("/teams"), api.get("/categories"), api.get("/jornadas")]);
        setTeams(t.data);
        setCats(c.data);
        setJornadas(j.data);
        // Load players scoped to my teams (backend already filters for coach)
        const p = await api.get("/players");
        setPlayers(p.data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  const myTeamIds = teams.map(t => t.id);
  const myPlayers = players.filter(p => myTeamIds.includes(p.team_id));
  const myJornadas = jornadas.filter(j => myTeamIds.includes(j.team_id));

  return (
    <div>
      <div className="mb-8">
        <div className="overline">Panel del entrenador</div>
        <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter uppercase mt-2">
          Hola, <span className="text-primary">{user?.name?.split(" ")[0]}</span>
        </h1>
        <p className="text-muted-foreground text-sm mt-2">Gestiona tus equipos y registra los datos de cada jornada.</p>
      </div>

      <div className="grid grid-tactical grid-cols-2 md:grid-cols-4 mb-10">
        <Kpi label="Mis equipos" value={teams.length} />
        <Kpi label="Jugadores" value={myPlayers.length} />
        <Kpi label="Jornadas" value={myJornadas.length} />
        <Kpi label="Partidos" value={myJornadas.filter(j => j.session_type === 'partido').length} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <QuickCard title="Registrar Jornada" desc="Anota resultados y estadísticas de un partido o entreno." to={teams.length === 1 ? `/coach/jornadas/new?team=${teams[0].id}` : "/coach/jornadas/new"} tid="quick-register-jornada" />
        <QuickCard title="Evaluaciones" desc="Evalúa el desarrollo individual de tus jugadores (frecuencia libre)." to="/evaluations" tid="quick-evaluations" />
        <QuickCard title="Estadísticas" desc="Consulta el rendimiento de tu equipo y jugadores." to="/stats" tid="quick-stats" />
        <QuickCard title="Comunicación" desc="Envía convocatorias, anuncios y RSVP a las familias." to="/comms" tid="quick-comms" />
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="overline">Mis equipos</div>
          <h2 className="font-heading font-bold text-2xl uppercase tracking-tight mt-1">Equipos asignados</h2>
        </div>
        <Link to="/coach/jornadas" data-testid="go-jornadas-btn" className="btn-volt">
          <Plus size={16} /> Registrar jornada
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map((t) => {
          const cat = cats.find(c => c.id === t.category_id);
          const playerCount = myPlayers.filter(p => p.team_id === t.id).length;
          const teamJornadas = myJornadas.filter(j => j.team_id === t.id);
          return (
            <Link key={t.id} to={`/team/${t.id}`} data-testid={`coach-team-${t.id}`} className="card-tactical p-5 block">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline flex items-center gap-1"><Trophy size={11} /> {cat?.name}</div>
                  <div className="font-heading font-black text-2xl uppercase tracking-tighter mt-1">{t.name}</div>
                </div>
                <ArrowUpRight size={16} className="text-primary" />
              </div>
              <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="overline">Jugadores</div>
                  <div className="metric-number text-2xl mt-1">{playerCount}</div>
                </div>
                <div>
                  <div className="overline">Jornadas</div>
                  <div className="metric-number text-2xl mt-1">{teamJornadas.length}</div>
                </div>
              </div>
            </Link>
          );
        })}
        {teams.length === 0 && (
          <div className="col-span-full text-center py-12 border border-dashed border-border">
            <Users className="mx-auto text-muted-foreground" size={28} />
            <div className="mt-3 text-muted-foreground text-sm">Aún no tienes equipos asignados. Contacta con el admin del club.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div data-testid={`coach-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`} className="p-6">
      <div className="overline">{label}</div>
      <div className="metric-number text-5xl mt-3">{value}</div>
    </div>
  );
}

function QuickCard({ title, desc, to, tid }) {
  return (
    <Link to={to} data-testid={tid} className="card-tactical p-5 flex items-start justify-between group">
      <div>
        <div className="font-heading font-bold uppercase tracking-tight text-lg">{title}</div>
        <div className="text-sm text-muted-foreground mt-1">{desc}</div>
      </div>
      <ArrowUpRight size={18} className="text-primary opacity-60 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
}
